import { useEffect, useMemo, useState } from 'react';

import { Button } from "@/components/ui/button";
import { Ban, Check, ChevronsUpDown, CircleQuestionMark, Eye, EyeOff, Info } from 'lucide-react';

import { BorderBox } from '@/components/atoms/BorderBox';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useAddCrmModalSimplifiedContext } from '@/features/crm/components/organisms/AddCrmModalSimplified/context/AddCrmModalSimplifiedContext';
import { useGetBasesForApiKey } from '@/features/crm_system/hooks/useQuery/airtable/useGetBasesForApiKey';
import { useGetTablesList } from '@/features/crm_system/hooks/useQuery/airtable/useGetTablesList';
import { AddCrmModalStepProps } from '@/features/crm_system/lib/_core/crmSystem';
import { TablesInsert } from '@/lib/supabase/supabase_types_override';
import { airtableDefaultConfigurationElements } from '@/lib/utils/crm/airtable/defaultConfiguration';
import { AirtableBase, AirtableTable } from "@/types/crm/airtable/common";
import { airtableRequiredPermissions } from '../../__utils/permissions';


export function FetchAirtableDataStepTitle() {
    return (
        <>
            Fetch Airtable Data
        </>
    )
}


export function FetchAirtableDataStep({ form }: Readonly<AddCrmModalStepProps>) {
    const selectedConfiguration = form.watch('selected_configuration.parameter') || [];

    useEffect(() => {
        const currentConfig = (form.getValues('selected_configuration.parameter') || []).filter(config => config != null);
        const existingApiKey = currentConfig.find(c => c?.id === 'Api Key');
        const otherConfigs = currentConfig.filter(c => c?.id !== 'Api Key');
        form.setValue('selected_configuration.parameter', [
            { id: 'Api Key', value: existingApiKey?.value ?? '' },
            ...otherConfigs
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selectedConfigurationApiKeyIndex = useMemo(() => {
        const idx = selectedConfiguration.findIndex(config => config?.id === 'Api Key');
        return idx === -1 ? selectedConfiguration.length : idx;
    }, [selectedConfiguration]);

    const apiKeyWatch = form.watch(`selected_configuration.parameter.${selectedConfigurationApiKeyIndex}.value`);

    const { isFetching: isFetchingBases, refetch: refetchBases, data: basesData, error: basesError } = useGetBasesForApiKey(apiKeyWatch ?? '')
    useEffect(() => {
        if (basesError) {
            if (basesError instanceof Error) {
                if (basesError.message === "AUTHENTICATION_REQUIRED") {
                    ErrorPopup("A valid Airtable Personal Access token is required to fetch bases. Please provide a valid API key and try again.")
                    return
                }
            }
            ErrorPopup("Error fetching Airtable bases")
        }
    }, [basesError])
    const { setIsCurrentStepValid, createProjectsAfterCrmCreation, setCreateProjectsAfterCrmCreation } = useAddCrmModalSimplifiedContext()

    useEffect(() => {
        setIsCurrentStepValid(!!basesData && !basesError);
    }, [setIsCurrentStepValid, basesError, basesData]);






    const [showValue, setShowValue] = useState(false);


    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-end gap-2">
                <FormField
                    control={form.control}
                    name={`selected_configuration.parameter.${selectedConfigurationApiKeyIndex}.value`}
                    render={({ field }) => (
                        <FormItem className='w-full'>
                            <FormLabel className='flex items-center gap-2'>
                                Airtable Personal Access Token
                                <HoverCard>
                                    <HoverCardTrigger asChild>
                                        <CircleQuestionMark size={16} className='text-blue-600' />
                                    </HoverCardTrigger>
                                    <HoverCardContent className='flex flex-col gap-2 w-96'>
                                        <b className=''>How can I obtain personal access token?</b>
                                        <ol className='flex flex-col gap-1 pl-4 list-decimal'>
                                            <li>Navigate to Airtable homepage</li>
                                            <li>In the top right corner, click on your profile icon</li>
                                            <li>Navigate to "Builder hub"</li>
                                            <li>Select "Personal access tokens" in the sidebar</li>
                                        </ol>
                                        <b className=''>What scopes are required?</b>
                                        <ol className='flex flex-col gap-1 pl-4 list-decimal'>
                                            <li>data.records:write - For exporting survey results to your Airtable Tables</li>
                                            <li>schema.bases:read - For reading your bases in order to automate creation of CRM Projects</li>
                                        </ol>
                                    </HoverCardContent>
                                </HoverCard>
                            </FormLabel>
                            <FormControl>
                                <div className="flex gap-2">
                                    <Input value={field.value ?? ''} onChange={field.onChange} type={showValue ? "text" : "password"} />
                                    <Button type="button" className="grid w-10 aspect-square place-items-center" variant='outline' onClick={() => setShowValue(prev => !prev)}  >
                                        {showValue
                                            ? <Eye className="" size={18} />
                                            : <EyeOff className="" size={18} />
                                        }
                                    </Button>
                                </div>
                            </FormControl>
                            <FormMessage />
                        </FormItem>
                    )}
                />
                <Button disabled={!apiKeyWatch?.length || isFetchingBases} onClick={() => refetchBases()} type="button">
                    {isFetchingBases && <Spinner />} Fetch data
                </Button>
            </div>

            <BorderBox variant='info' size='sm' className="text-xs">
                If you don't see the bases you want to connect to, make sure you have the correct permissions.
            </BorderBox>
            {(basesData && 'bases' in basesData && basesData.bases.length > 0) && (
                <BorderBox size='sm' variant='info' className="text-xs">
                    Select bases/tables on the right using checkboxes to automatically create Project / Survey Connections for them. You can edit the created connections later to fine-tune the configuration.
                </BorderBox>
            )}
            <TooltipProvider>
                {(() => {
                    if (basesError) {
                        return (
                            <BorderBox size='sm' variant='info' icon={false}>
                                <div className="flex items-center gap-2 text-xs text-red-600">
                                    <Ban size="16" />
                                    <div className="flex flex-col ">
                                        <span className="font-bold">Error fetching bases</span>
                                        <span>{basesError instanceof Error ? basesError.message : 'Unknown error'}</span>
                                    </div>
                                </div>
                            </BorderBox>
                        )
                    }
                    if (isFetchingBases) {
                        return (
                            <BorderBox size='sm' variant='info'>
                                <div className="flex items-center gap-2 text-xs text-blue-600">
                                    <Spinner className="w-4 h-4 animate-spin" />
                                    <div className="flex flex-col ">
                                        <span className="font-bold">Fetching bases...</span>
                                    </div>
                                </div>
                            </BorderBox>
                        )
                    }
                    if (basesData && 'bases' in basesData) {
                        if (basesData.bases.length === 0) {
                            return (
                                <BorderBox size='sm' variant='info'>
                                    <div className="flex items-center gap-2 text-xs text-blue-600">
                                        <Info size="16" />
                                        <div className="flex flex-col ">
                                            <span className="font-bold">No bases found</span>
                                            <span>Your Airtable account does not have any bases. Please create a base in Airtable and try again.</span>
                                        </div>
                                    </div>
                                </BorderBox>
                            )
                        }
                        return (
                            <>
                                <div className="flex justify-end">
                                    <Button
                                        variant='outline'
                                        size='sm'
                                        type='button'
                                        onClick={(e) => {
                                            if (e.target !== e.currentTarget) return;
                                            e.stopPropagation();
                                            setCreateProjectsAfterCrmCreation(prev => !prev);
                                        }}
                                        disabled={isFetchingBases}
                                    >
                                        <Checkbox
                                            checked={createProjectsAfterCrmCreation}
                                            className='size-4'
                                        />
                                        Create projects and surveys automatically for all selected bases/tables
                                    </Button>
                                </div>
                                {
                                    basesData.bases.map(base => (
                                        <AirtableBaseBox key={base.id} base={base} form={form} />
                                    ))
                                }
                            </>
                        )
                    }
                })()}
            </TooltipProvider>
        </div>
    )
}

interface AirtableBaseProps extends AddCrmModalStepProps {
    base: AirtableBase
}

function AirtableBaseBox({ base, form }: AirtableBaseProps) {
    const crmProjectsToCreate = form.watch("crm_project")
    const apiKeyWatch = form.watch('selected_configuration.parameter')?.find((config) => config?.id === 'Api Key')?.value;

    const [isBaseIncluded, setIsBaseIncluded] = useState(() =>
        !!(form.getValues("crm_project") ?? []).find((p) =>
            p.selected_configuration?.parameter?.find((param) => param.value === base.id)
        )
    );
    const [isCollapsibleOpen, setIsCollapsibleOpen] = useState(false);

    const addCrmProjectForCreation = (crmProject: Omit<TablesInsert<'crm_project'>, 'crm_id'>) => {
        form.setValue("crm_project", [...(crmProjectsToCreate ?? []), crmProject])
    }
    const removeCrmProjectForCreation = (baseId: string) => {
        form.setValue("crm_project", (crmProjectsToCreate ?? []).filter(existingCrmProject => existingCrmProject.selected_configuration?.parameter?.findIndex(parameter =>
            parameter.value === baseId
        ) === -1))
    }
    const selectedCrmProjectData = crmProjectsToCreate
        ?.find(crmProject =>
            crmProject
                .selected_configuration
                ?.parameter
                ?.find(parameter =>
                    parameter.value === base.id
                )
        );
    const crmProjectSurveyConfigCount = selectedCrmProjectData?.crm_config?.length || 0;

    const hasPermissions = airtableRequiredPermissions.includes(base.permissionLevel);

    const { data: tablesData, isFetching: isFetchingTables, error: tablesError } = useGetTablesList({ apiKey: selectedCrmProjectData ? apiKeyWatch! : undefined, baseId: base.id });

    const allTablesSelected = tablesData?.tables.every(table =>
        selectedCrmProjectData?.crm_config?.some(crmConfig =>
            crmConfig.selected_configuration?.parameter?.some(parameter =>
                parameter.value === table.id
            ))
    );
    const someTablesSelected = tablesData?.tables.some(table =>
        selectedCrmProjectData?.crm_config?.some(crmConfig =>
            crmConfig.selected_configuration?.parameter?.some(parameter =>
                parameter.value === table.id
            ))
    );

    const tablesSelectedCheckboxCheckedState = allTablesSelected ? true : someTablesSelected ? "indeterminate" : false;

    const baseCheckboxCheckedState: boolean | "indeterminate" = !isBaseIncluded
        ? false
        : tablesData && !allTablesSelected
            ? "indeterminate"
            : true;

    const toggleSelectAllTablesForThisBase = () => {
        const latestCrmProjects = form.getValues("crm_project") || [];
        const projectIndex = latestCrmProjects.findIndex(crmProject =>
            crmProject.selected_configuration?.parameter?.some(parameter =>
                parameter.value === base.id
            )
        );
        if (projectIndex === -1) return;

        const projectData = latestCrmProjects[projectIndex];

        if (allTablesSelected) {
            // Deselect all
            const updatedCrmProject = {
                ...projectData,
                crm_config: (projectData.crm_config || []).filter(crmConfig =>
                    !tablesData?.tables.some(table =>
                        crmConfig.selected_configuration?.parameter?.some(parameter =>
                            parameter.value === table.id
                        )
                    )
                )
            };
            const updatedCrmProjects = [...latestCrmProjects];
            updatedCrmProjects[projectIndex] = updatedCrmProject;
            form.setValue("crm_project", updatedCrmProjects);
        } else {
            // Select all
            const updatedCrmProject = {
                ...projectData,
                crm_config: [
                    ...(projectData.crm_config || []),
                    ...tablesData!.tables.filter(table =>
                        !(projectData.crm_config || []).some(crmConfig =>
                            crmConfig.selected_configuration?.parameter?.some(parameter =>
                                parameter.value === table.id
                            )
                        )
                    ).map(table => ({
                        name: table.name,
                        selected_configuration: {
                            "parameter": [
                                {
                                    "value": table.id,
                                    "id": airtableDefaultConfigurationElements.parameter.survey.tableName
                                }
                            ]
                        }
                    }))
                ]
            };
            const updatedCrmProjects = [...latestCrmProjects];
            updatedCrmProjects[projectIndex] = updatedCrmProject;
            form.setValue("crm_project", updatedCrmProjects);
        }
    };

    return (
        <BorderBox icon={false} className="flex-col items-start gap-0" variant={hasPermissions ? 'default' : 'danger'} size='sm'>
            <div className="flex items-center justify-between w-full">
                <div className="grid w-4/5 grid-cols-2">
                    <div className="text-neutral-900">
                        <div >Base:<span className="font-bold">{" "}{base.name}</span> </div>
                        <div className="text-xs text-muted-foreground">ID: {base.id}</div>
                    </div>
                    {hasPermissions ? (
                        <div className="flex items-center gap-2 text-xs text-green-600">
                            <Check size="16" />
                            <div className="flex flex-col ">
                                <span className="font-bold">Access confirmed</span>
                                <span>You have permission to create content.</span>
                            </div>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-xs text-red-600">
                            <Ban size="16" />
                            <div className="flex flex-col ">
                                <span className="font-bold">Access restricted</span>
                                <span>You don’t have the necessary permissions. You have <b>[{base.permissionLevel}]</b> but need <b>[{airtableRequiredPermissions.join('/ ')}]</b>.</span>
                            </div>
                        </div>
                    )}
                </div>
                <Tooltip>
                    <TooltipTrigger asChild>
                        <span>
                            {isFetchingTables && isBaseIncluded
                                ? <Spinner />
                                : <Checkbox
                                    checked={baseCheckboxCheckedState}
                                    disabled={!hasPermissions}
                                    onCheckedChange={(checked) => {
                                        if (checked && !isBaseIncluded) {
                                            setIsBaseIncluded(true);
                                            addCrmProjectForCreation({
                                                name: base.name,
                                                selected_configuration: {
                                                    "parameter": [
                                                        {
                                                            "value": base.id,
                                                            "id": airtableDefaultConfigurationElements.parameter.project.baseId
                                                        }
                                                    ]
                                                }
                                            });
                                        } else if (!checked && isBaseIncluded) {
                                            setIsBaseIncluded(false);
                                            removeCrmProjectForCreation(base.id);
                                        }
                                    }}
                                />
                            }
                        </span>
                    </TooltipTrigger>
                    <TooltipContent>
                        {selectedCrmProjectData ? `Don't create CRM Project for this base` : 'Create CRM Project for this base'}
                    </TooltipContent>
                </Tooltip>
            </div>
            <Collapsible className='w-full' open={isCollapsibleOpen} onOpenChange={setIsCollapsibleOpen}>

                {selectedCrmProjectData && (
                    <div className='mt-2'>
                        {(() => {
                            if (tablesError) {
                                return (
                                    <div className="text-sm text-red-500">Error loading Airtable Table Data: {tablesError.message}</div>
                                )
                            }
                            if (isFetchingTables) {
                                return (
                                    <div className="flex gap-2 text-sm text-muted-foreground"><Spinner /> Loading Airtable Data...</div>
                                )
                            }
                            if (tablesData?.tables.length === 0) {
                                return (
                                    <div className="mt-2 text-sm">
                                        No tables found.
                                    </div>
                                )
                            }
                            if (tablesData?.tables.length) {
                                return (
                                    <BorderBox size='sm' className="justify-between w-full p-1 px-2 bg-white cursor-pointer">
                                        <div>
                                            <p className="text-xs">Configuration for <span className="font-bold">{crmProjectSurveyConfigCount} table{crmProjectSurveyConfigCount === 1 ? '' : 's'}</span> from <span className="font-bold">{tablesData?.tables.length}</span> available will be created</p>
                                            {/* <p className="text-xs font-semibold text-blue-500">Click here to view all available tables</p> */}
                                        </div>
                                        <div className="flex items-center gap-1">

                                            <Button
                                                // className={buttonVariants({ variant: 'outline', size: 'sm' })}
                                                type='button'
                                                onClick={(e) => {
                                                    console.log(e)
                                                    if (e.target !== e.currentTarget) return;
                                                    e.stopPropagation();
                                                    // e.preventDefault();
                                                    toggleSelectAllTablesForThisBase();
                                                }}
                                                size='sm'
                                                variant='outline'
                                            >
                                                <Checkbox
                                                    checked={tablesSelectedCheckboxCheckedState}
                                                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                                                    className='size-4'
                                                />
                                                {allTablesSelected ? 'Unselect All' : 'Select All'}
                                            </Button>

                                            <CollapsibleTrigger asChild>
                                                <Button variant='outline' size='action' type='button'>
                                                    <ChevronsUpDown size={16} />
                                                </Button>
                                            </CollapsibleTrigger>
                                        </div>
                                    </BorderBox>
                                )
                            }
                        })()}
                    </div>
                )}
                <CollapsibleContent>
                    {selectedCrmProjectData && tablesData && tablesData.tables.length > 0 && (
                        <div className="flex flex-col w-full gap-1 mt-2">
                            {tablesData?.tables.map((table) => (
                                <AirtableTableBox key={table.id} table={table} base={base} form={form} />
                            ))}
                        </div>
                    )}
                </CollapsibleContent>
            </Collapsible>
        </BorderBox>
    )
}

interface AirtableTableBoxProps extends AddCrmModalStepProps {
    table: AirtableTable;
    base: AirtableBase;
}

function AirtableTableBox({ table, base, form }: Readonly<AirtableTableBoxProps>) {
    const crmProjectsToCreate = form.watch("crm_project")
    // console.log("[AirtableTableBox] crmProjectsToCreate: ", crmProjectsToCreate)
    const parentCrmProject = crmProjectsToCreate
        ?.find(crmProject =>
            crmProject
                .selected_configuration
                ?.parameter
                ?.find(parameter =>
                    parameter.value === base.id
                )
        );
    const crmConfigsForThisProject = parentCrmProject?.crm_config || [];
    const isTableIncluded = crmConfigsForThisProject
        .find(crmConfig =>
            crmConfig
                .selected_configuration
                ?.parameter
                ?.find(parameter =>
                    parameter.value === table.name || parameter.value === table.id
                )
        );

    const addCrmConfigForCreation = (crmConfig: Omit<TablesInsert<'crm_config'>, 'crm_id'>, baseId: string) => {
        const crmProjectsToCreate = form.getValues("crm_project") || [];
        const projectIndex = crmProjectsToCreate.findIndex(existingCrmProject => existingCrmProject.selected_configuration?.parameter?.findIndex(parameter =>
            parameter.value === baseId
        ) !== -1);
        if (projectIndex === -1) {
            return;
        }
        const updatedCrmProject = {
            ...crmProjectsToCreate[projectIndex],
            crm_config: [
                ...(crmProjectsToCreate[projectIndex].crm_config || []),
                crmConfig
            ]
        };
        const updatedCrmProjects = [...crmProjectsToCreate];
        updatedCrmProjects[projectIndex] = updatedCrmProject;
        form.setValue("crm_project", updatedCrmProjects);
    }
    const removeCrmConfigForCreation = (tableNameOrId: string, baseId: string) => {
        const crmProjectsToCreate = form.getValues("crm_project") || [];
        const projectIndex = crmProjectsToCreate.findIndex(existingCrmProject => existingCrmProject.selected_configuration?.parameter?.findIndex(parameter =>
            parameter.value === baseId
        ) !== -1);
        if (projectIndex === -1) {
            return;
        }
        const updatedCrmProject = {
            ...crmProjectsToCreate[projectIndex],
            crm_config: (crmProjectsToCreate[projectIndex].crm_config || []).filter(existingCrmConfig => existingCrmConfig.selected_configuration?.parameter?.findIndex(parameter => {
                // console.log(parameter.value === tableNameOrId)
                return parameter.value === tableNameOrId
            }) === -1)
        };
        const updatedCrmProjects = [...crmProjectsToCreate];
        updatedCrmProjects[projectIndex] = updatedCrmProject;
        form.setValue("crm_project", updatedCrmProjects);
    }
    return (
        <BorderBox key={table.id} className="justify-between bg-white" size='sm'>
            <div className="flex flex-col gap-0.5">
                <div className="text-xs font-bold">{table.name}</div>
                <div className="text-xs text-muted-foreground">ID: {table.id}</div>
            </div>
            <Tooltip>
                <TooltipTrigger asChild>
                    <span>
                        <Checkbox
                            checked={!!isTableIncluded}
                            onCheckedChange={(checked) => {
                                if (checked) {
                                    addCrmConfigForCreation({
                                        name: table.name,
                                        selected_configuration: {
                                            "parameter": [
                                                {
                                                    "value": table.id,
                                                    "id": airtableDefaultConfigurationElements.parameter.survey.tableName
                                                }
                                            ]
                                        }
                                    }, base.id);
                                } else {
                                    removeCrmConfigForCreation(table.id, base.id);
                                }
                            }}
                            onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        />
                    </span>
                </TooltipTrigger>
                <TooltipContent>
                    {isTableIncluded ? `Don't create CRM Config` : 'Create CRM Config for this table'}
                </TooltipContent>
            </Tooltip>
        </BorderBox>
    )
}